import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import { Briefcase, Gauge, Users, Fingerprint } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { getHomepageWhy } from "../lib/sanityQueries";
import { peek } from "../lib/sanity";

const iconByPosition = [Briefcase, Fingerprint, Users, Gauge];

const renderWhyHeadline = (text) => {
    const idx = text.indexOf(",");
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx + 1)}
            <span className="text-brand-accent">{text.slice(idx + 1)}</span>
        </>
    );
};

const points = [
    {
        icon: Briefcase,
        title: "Business-first thinking",
        body: "We focus on business outcomes, not just technology solutions. Every decision considers operations, growth, and risk.",
    },
    {
        icon: Fingerprint,
        title: "Enterprise standards",
        body: "Enterprise-grade tools, security, and operational practices — the operational maturity of a large organization, sized for growing businesses.",
    },
    {
        icon: Users,
        title: "Trusted partnership",
        body: "Founder-led and accountable, we work as an extension of your leadership team — not simply a vendor.",
    },
    {
        icon: Gauge,
        title: "Proactive by design",
        body: "We prevent issues, reduce risk, and create operational stability before problems occur.",
    },
];

export const WhyEasilinked = () => {
    const [why, setWhy] = useState(() => peek(getHomepageWhy));
    useEffect(() => {
        let active = true;
        getHomepageWhy()
            .then((data) => {
                if (active && data) setWhy(data);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    const cards = why?.differentiators?.length
        ? why.differentiators.map((d, i) => ({
              icon: iconByPosition[i] || iconByPosition[iconByPosition.length - 1],
              title: d.title,
              body: d.description,
          }))
        : points;

    return (
    <section
        id="why"
        data-testid="why-section"
        className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36"
    >
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                    <Reveal>
                        <Overline>Why EasiLinked</Overline>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                            {why?.whyEasiLinkedHeadline ? (
                                renderWhyHeadline(why.whyEasiLinkedHeadline)
                            ) : (
                                <>
                                    Technology leadership,
                                    <span className="text-brand-accent"> minus the overhead.</span>
                                </>
                            )}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-base leading-relaxed text-brand-muted">
                            {why?.whyEasiLinkedMessage ||
                                "We bring enterprise-grade tools, security, and operational practices to growing businesses — working as an extension of your leadership team, not simply a vendor."}
                        </p>
                    </Reveal>
                </div>
            </div>

            <div className="lg:col-span-8">
                <div className="grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-brand-line bg-brand-line sm:grid-cols-2">
                    {cards.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.06} className="bg-brand-bg">
                            <motion.div
                                whileHover={{ backgroundColor: "var(--el-surface)" }}
                                className="h-full p-8 sm:p-10"
                            >
                                <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line text-brand-accent">
                                    <p.icon className="h-5 w-5" />
                                </span>
                                <h3 className="mt-6 font-heading text-xl font-bold tracking-tight text-brand-text">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                                    {p.body}
                                </p>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </section>
    );
};
